import { useMutation, useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';

import { gqlClient } from '@/api';
import { type ApiTokenScope, unwire, wire } from '@/lib/api-tokens';

/**
 * The consent page's half of an OAuth sign-in: what is being asked, and the
 * account's answer. ChatGPT or Claude sent the browser here; the API parked
 * their request under an id; the answer is the address to send the browser
 * back to.
 */

export interface OAuthPendingRequest {
  id: string;
  clientName: string;
  redirectHost: string;
  scopes: ApiTokenScope[];
  expiresAt: string;
}

const REQUEST = gql`
  query OAuthRequest($id: ID!) {
    oauthRequest(id: $id) {
      id
      clientName
      redirectHost
      scopes
      expiresAt
    }
  }
`;
const APPROVE = gql`
  mutation ApproveOAuthRequest($input: ApproveOAuthRequestInput!) {
    approveOAuthRequest(input: $input)
  }
`;
const DENY = gql`
  mutation DenyOAuthRequest($id: ID!) {
    denyOAuthRequest(id: $id)
  }
`;

/** Null once the request has expired or been answered. */
export const useOAuthRequest = (id: string | null) =>
  useQuery({
    queryKey: ['oauthRequest', id],
    enabled: id !== null,
    // Never twice: a request answered once is gone, and asking again would say so.
    retry: false,
    staleTime: Infinity,
    queryFn: async () => {
      const { oauthRequest } = await gqlClient.request<{
        oauthRequest: OAuthPendingRequest | null;
      }>(REQUEST, { id });
      return oauthRequest ? { ...oauthRequest, scopes: oauthRequest.scopes.map(unwire) } : null;
    },
  });

/** Resolves to the address to send the browser to. */
export const useApproveOAuthRequest = () =>
  useMutation({
    mutationFn: async (input: { id: string; scopes: ApiTokenScope[]; password: string }) =>
      (
        await gqlClient.request<{ approveOAuthRequest: string }>(APPROVE, {
          input: { ...input, scopes: input.scopes.map(wire) },
        })
      ).approveOAuthRequest,
  });

/** Resolves to the address to send the browser to, or null when there was nothing left to refuse. */
export const useDenyOAuthRequest = () =>
  useMutation({
    mutationFn: async (id: string) =>
      (await gqlClient.request<{ denyOAuthRequest: string | null }>(DENY, { id })).denyOAuthRequest,
  });
